import React, { useState } from 'react';

import { useTranslation } from '@shared/core';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { Box, ButtonV2, SpacingStack, TextV2 } from '@withjoy/joykit';

import { useShoppingCart } from '@apps/registry/guest/routes/GuestRegistry/components/ShoppingCart/state';
import { ShippingAddressOptions } from '@apps/registry/guest/routes/GuestRegistry/components/ShoppingCart';
import { CartFooterSubmitButton, ShippingAddressAtom, WarningAddressAtom } from '@apps/registry/guest/routes/GuestRegistry/components/ShoppingCart/components';

import { AddressForm } from './AddressForm';
import { useAddressFormFields } from './hooks';
import { AddressFormFields } from './types';
import { AddressUsedEnum } from '@apps/registry/common/components/AddressDialog/types';
import { StandardShippingOption } from '../../../components/AddressAtoms/StandardShipping';

export const ShippingStep = ({ disableNextStep }: { disableNextStep?: boolean }) => {
  const isMobileOrTablet = useIsMobileOrTablet();

  const { t2 } = useTranslation('guestRegistry');
  const { shippingMethod: shippingMethodLabel, freeShipping, shipTo, giftEmoji, onlyUSAddress, shipToMe, weWillTellAboutPurchase, standardShipping } = t2(
    'shoppingCart',
    'shippingAddressStep'
  );
  const { editButton } = t2('shoppingCart', 'shippingAddressStep', 'addressForm');

  const {
    state: { customShippingAddress, coupleAddress, selectedShippingOption, shippingFee },
    mutators: { updateShippingAddress: updateShippingAddressForm, updateShippingOption }
  } = useShoppingCart();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [shippingOptionSelected, setShippingOptionSelected] = useState<ShippingAddressOptions>(selectedShippingOption);
  const [, setSuggestedAddress] = useState<AddressFormFields | null>(null);
  const [, setAddressUsed] = useState<AddressUsedEnum>(AddressUsedEnum.CUSTOM);
  const [shippingAddressFormValues, setShippingAddressFormValues] = useState<AddressFormFields>(customShippingAddress as AddressFormFields);

  const handleFormSubmit = (formValues: AddressFormFields) => {
    setShippingAddressFormValues(formValues);
    updateShippingAddressForm(formValues);
    updateShippingOption(shippingOptionSelected);
  };
  const { saveAddress, formik, ...formFields } = useAddressFormFields(shippingAddressFormValues, handleFormSubmit);

  const createAddressLabel = (address: string) => {
    return `${giftEmoji} ${address}`;
  };

  return (
    <SpacingStack spacing={6} width="100%">
      <div>
        <TextV2 typographyVariant="label3" marginBottom={3}>
          {shippingMethodLabel}
        </TextV2>
        <StandardShippingOption label={shippingFee === '0.00' ? freeShipping : standardShipping} value={shippingFee ? '$' + shippingFee : ''} />
      </div>
      <div>
        <TextV2 typographyVariant="label3" marginBottom={3}>
          {shipTo}
        </TextV2>
        <SpacingStack spacing={3} width="100%">
          {!coupleAddress?.name ? (
            <>
              <WarningAddressAtom />
            </>
          ) : (
            <ShippingAddressAtom
              isSelected={shippingOptionSelected === ShippingAddressOptions.CoupleAddress}
              label={createAddressLabel(coupleAddress.name)}
              note={`${coupleAddress.address1}, ${coupleAddress.address2 ? coupleAddress.address2 + ', ' : ''}${coupleAddress.city}, ${coupleAddress.postalCode}`}
              onSelect={() => {
                updateShippingOption(ShippingAddressOptions.CoupleAddress);
                setShippingOptionSelected(ShippingAddressOptions.CoupleAddress);
                setIsFormOpen(false);
              }}
            />
          )}
          <ShippingAddressAtom
            isSelected={shippingOptionSelected === ShippingAddressOptions.ShipToMe}
            label={createAddressLabel(shipToMe)}
            note={weWillTellAboutPurchase}
            tooltip={onlyUSAddress}
            onSelect={() => {
              setShippingOptionSelected(ShippingAddressOptions.ShipToMe);
              if (customShippingAddress?.name) {
                updateShippingOption(ShippingAddressOptions.ShipToMe);
              } else {
                setIsFormOpen(true);
              }
            }}
            address={shippingAddressFormValues?.name ? shippingAddressFormValues : undefined}
          />
        </SpacingStack>
        {isFormOpen ? (
          <Box marginTop={6}>
            <AddressForm
              handleCancelClick={() => {
                setIsFormOpen(false);
              }}
              handleSaveClick={() => {
                setIsFormOpen(false);
                saveAddress();
              }}
              formFields={formFields}
              setSuggestedAddress={setSuggestedAddress}
              setAddressUsed={setAddressUsed}
              formik={formik}
            />
          </Box>
        ) : (
          !!shippingAddressFormValues?.name && (
            <ButtonV2 marginTop={6} intent="neutral" variant="outline" onClick={() => setIsFormOpen(true)} fullWidth>
              {editButton}
            </ButtonV2>
          )
        )}
        {!isMobileOrTablet && !isFormOpen && <CartFooterSubmitButton disabled={disableNextStep} />}
      </div>
    </SpacingStack>
  );
};
